<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';

const router = useRouter();
const store = useStore();
const { accountId, accountScopedRoute } = useAccount();
const inboxes = useMapGetter('inboxes/getInboxes');

const pageCopy = {
  eyebrow: 'Primeiros passos',
  title: 'Comece com a Talkvia',
  subtitle:
    'Um roteiro direto para conectar canais, cadastrar contatos, enviar mensagens, preparar campanhas e criar sua primeira Agente de IA.',
  primaryAction: 'Conectar canais',
  guidedAction: 'Iniciar tour guiado',
  manualAction: 'Abrir manual completo',
  skip: 'Fechar',
  previous: 'Voltar',
  next: 'Próximo',
  finish: 'Concluir',
  openStep: 'Abrir esta tela',
  progressLabel: 'Passo',
};

const activeIntroStep = ref(0);
const isIntroOpen = ref(false);
const spotlightStyle = ref({});
const tooltipStyle = ref({});

const inboxList = computed(() => inboxes.value || []);
const firstInbox = computed(() => inboxList.value[0]);
const officialChannelTypes = [
  'Channel::Whatsapp',
  'Channel::Instagram',
  'Channel::FacebookPage',
];
const connectedOfficialChannels = computed(
  () =>
    inboxList.value.filter(inbox =>
      officialChannelTypes.includes(inbox.channel_type)
    ).length
);

const hasInboxes = computed(() => inboxList.value.length > 0);
const channelRoute = subPage =>
  accountScopedRoute('settings_inboxes_page_channel', {
    sub_page: subPage,
  });

const quickStats = computed(() => [
  {
    label: 'Conexões',
    value: String(inboxList.value.length),
    icon: 'i-lucide-plug',
    tone: hasInboxes.value ? 'text-n-teal-11' : 'text-n-amber-11',
  },
  {
    label: 'Canais oficiais',
    value: `${connectedOfficialChannels.value}/3`,
    icon: 'i-lucide-badge-check',
    tone: connectedOfficialChannels.value
      ? 'text-n-teal-11'
      : 'text-n-slate-11',
  },
  {
    label: 'Próximo passo',
    value: hasInboxes.value ? 'Mensagens' : 'Conexão',
    icon: 'i-lucide-route',
    tone: 'text-n-blue-11',
  },
]);

const primarySteps = computed(() => [
  {
    number: '01',
    tourId: 'connection',
    title: 'Conectar canais oficiais',
    description:
      'Comece pelo WhatsApp Oficial e deixe Instagram e Messenger prontos para atendimento.',
    icon: 'i-lucide-badge-check',
    status: connectedOfficialChannels.value ? 'Em andamento' : 'Primeiro passo',
    route: channelRoute('whatsapp'),
    action: 'Conectar WhatsApp Oficial',
    accent: 'border-n-blue-7 bg-n-blue-2 text-n-blue-11',
  },
  {
    number: '02',
    tourId: 'contacts',
    title: 'Cadastrar contatos',
    description:
      'Organize nomes, telefones, etiquetas e histórico antes da primeira campanha.',
    icon: 'i-lucide-users-round',
    status: 'Base comercial',
    route: accountScopedRoute('contacts_dashboard_index', {}, { page: 1 }),
    action: 'Abrir contatos',
    accent: 'border-n-teal-7 bg-n-teal-2 text-n-teal-11',
  },
  {
    number: '03',
    tourId: 'messages',
    title: 'Enviar a primeira mensagem',
    description:
      'Use Nova conversa para validar atendimento, anexos e transferência entre agentes.',
    icon: 'i-lucide-message-square-plus',
    status: hasInboxes.value ? 'Disponível' : 'Após conexão',
    route: firstInbox.value
      ? accountScopedRoute('inbox_dashboard', { inbox_id: firstInbox.value.id })
      : accountScopedRoute('home'),
    action: 'Ir para conversas',
    accent: 'border-n-violet-7 bg-n-violet-2 text-n-violet-11',
  },
]);

const growthSteps = computed(() => [
  {
    tourId: 'templates',
    title: 'Templates WhatsApp Oficial',
    description:
      'Revise modelos aprovados pela Meta e prepare mensagens ativas para clientes.',
    icon: 'i-lucide-file-check-2',
    route: accountScopedRoute('campaigns_whatsapp_templates'),
    action: 'Ver templates',
  },
  {
    tourId: 'campaigns',
    title: 'Campanhas WhatsApp Oficial',
    description:
      'Depois dos contatos e templates, envie comunicados segmentados pelo WhatsApp Oficial.',
    icon: 'i-lucide-send',
    route: accountScopedRoute('campaigns_whatsapp_index'),
    action: 'Abrir campanhas',
  },
  {
    tourId: 'instagram',
    title: 'Instagram',
    description:
      'Centralize directs, histórico e atendimento do Instagram na mesma operação.',
    icon: 'i-brand-instagram',
    route: channelRoute('instagram'),
    action: 'Conectar Instagram',
  },
  {
    tourId: 'messenger',
    title: 'Messenger',
    description:
      'Conecte sua página do Facebook para responder mensagens pelo Messenger.',
    icon: 'i-lucide-messages-square',
    route: channelRoute('facebook'),
    action: 'Conectar Messenger',
  },
  {
    tourId: 'openai',
    title: 'Criar sua primeira Agente de IA',
    description:
      'Siga o tutorial para conectar a OpenAI, escolher um modelo, salvar o prompt e vincular a agente ao WhatsApp.',
    icon: 'i-lucide-brain-circuit',
    route: accountScopedRoute('talkvia_openai'),
    action: 'Abrir Biblioteca de IA',
  },
]);

const introStorageKey = computed(
  () => `talkvia-guided-tour-seen-${accountId.value}`
);
const activeTourStorageKey = computed(
  () => `talkvia-guided-tour-active-${accountId.value}`
);
const introSteps = computed(() => [
  {
    selector: '[data-tour-id="overview"]',
    title: 'Conheça o caminho principal',
    description:
      'Este tour mostra a ordem recomendada para começar: canais oficiais, contatos, mensagens, campanhas e IA.',
  },
  {
    selector: '[data-tour-id="connection"]',
    title: 'Conecte canais oficiais',
    description:
      'O primeiro passo é ativar WhatsApp Oficial, Instagram e Messenger para centralizar o atendimento.',
    route: channelRoute('whatsapp'),
    globalStep: 0,
  },
  {
    selector: '[data-tour-id="contacts"]',
    title: 'Cadastre ou importe contatos',
    description:
      'Aqui você organiza clientes, etiquetas e histórico antes de iniciar atendimentos ou campanhas.',
    route: accountScopedRoute('contacts_dashboard_index', {}, { page: 1 }),
    globalStep: 1,
  },
  {
    selector: '[data-tour-id="messages"]',
    title: 'Envie a primeira mensagem',
    description:
      'Depois da conexão, use as conversas para testar atendimento, anexos e transferência entre agentes.',
    route: firstInbox.value
      ? accountScopedRoute('inbox_dashboard', { inbox_id: firstInbox.value.id })
      : accountScopedRoute('home'),
    globalStep: 2,
  },
  {
    selector: '[data-tour-id="templates"]',
    title: 'Prepare templates WhatsApp Oficial',
    description:
      'Templates aprovados pela Meta ajudam no envio ativo e deixam campanhas mais previsíveis.',
    route: accountScopedRoute('campaigns_whatsapp_templates'),
    globalStep: 3,
  },
  {
    selector: '[data-tour-id="instagram"]',
    title: 'Ative Instagram e Messenger',
    description:
      'Depois do WhatsApp Oficial, conecte Instagram e Messenger para cobrir os canais sociais da operação.',
    route: channelRoute('instagram'),
    globalStep: 4,
  },
  {
    selector: '[data-tour-id="openai"]',
    title: 'Crie sua primeira Agente de IA',
    description:
      'Abra a Biblioteca de IA para seguir o tutorial, escolher um modelo pronto, ativar o prompt e depois vincular a agente ao WhatsApp.',
    route: accountScopedRoute('talkvia_openai'),
    globalStep: 5,
  },
]);

const currentIntroStep = computed(
  () => introSteps.value[activeIntroStep.value]
);
const isLastIntroStep = computed(
  () => activeIntroStep.value === introSteps.value.length - 1
);
const introProgressText = computed(
  () =>
    `${pageCopy.progressLabel} ${activeIntroStep.value + 1} de ${
      introSteps.value.length
    }`
);

const openRoute = route => {
  router.push(route);
};

const markIntroSeen = () => {
  localStorage.setItem(introStorageKey.value, 'true');
};

const updateSpotlight = ({ scroll = false } = {}) => {
  if (!isIntroOpen.value) return;

  const step = currentIntroStep.value;
  const element = document.querySelector(step?.selector);
  if (!element) return;

  if (scroll) {
    element.scrollIntoView({
      block: 'center',
      inline: 'center',
      behavior: 'smooth',
    });
  }

  window.setTimeout(() => {
    const rect = element.getBoundingClientRect();
    const margin = 8;
    const tooltipWidth = Math.min(360, window.innerWidth - 32);
    const maxTooltipTop = Math.max(window.innerHeight - 280, 16);
    const leftCandidate =
      rect.right + tooltipWidth + 24 < window.innerWidth
        ? rect.right + 16
        : rect.left;
    const topCandidate =
      rect.right + tooltipWidth + 24 < window.innerWidth
        ? rect.top
        : rect.bottom + 16;

    spotlightStyle.value = {
      top: `${Math.max(rect.top - margin, 12)}px`,
      left: `${Math.max(rect.left - margin, 12)}px`,
      width: `${Math.min(rect.width + margin * 2, window.innerWidth - 24)}px`,
      height: `${rect.height + margin * 2}px`,
    };
    tooltipStyle.value = {
      top: `${Math.min(Math.max(topCandidate, 16), maxTooltipTop)}px`,
      left: `${Math.min(Math.max(leftCandidate, 16), window.innerWidth - tooltipWidth - 16)}px`,
      width: `${tooltipWidth}px`,
    };
  }, 260);
};

const startIntro = ({ force = true } = {}) => {
  if (!force && localStorage.getItem(introStorageKey.value)) return;
  activeIntroStep.value = 0;
  isIntroOpen.value = true;
  nextTick(() => updateSpotlight({ scroll: true }));
};

const closeIntro = () => {
  isIntroOpen.value = false;
  markIntroSeen();
};

const nextIntroStep = () => {
  if (isLastIntroStep.value) {
    closeIntro();
    return;
  }
  activeIntroStep.value += 1;
};

const previousIntroStep = () => {
  if (activeIntroStep.value > 0) activeIntroStep.value -= 1;
};

const openCurrentIntroRoute = () => {
  const step = currentIntroStep.value;
  const route = step?.route;
  if (!route) return;
  if (Number.isInteger(step.globalStep)) {
    localStorage.setItem(
      activeTourStorageKey.value,
      JSON.stringify({ step: step.globalStep })
    );
  }
  isIntroOpen.value = false;
  markIntroSeen();
  openRoute(route);
};

const handleIntroKeydown = event => {
  if (!isIntroOpen.value) return;
  if (event.key === 'Escape') closeIntro();
  if (event.key === 'ArrowRight') nextIntroStep();
  if (event.key === 'ArrowLeft') previousIntroStep();
};

onMounted(() => {
  store.dispatch('inboxes/get');
  window.addEventListener('resize', updateSpotlight);
  window.addEventListener('keydown', handleIntroKeydown);
  nextTick(() => startIntro({ force: false }));
});

onUnmounted(() => {
  window.removeEventListener('resize', updateSpotlight);
  window.removeEventListener('keydown', handleIntroKeydown);
});

watch(activeIntroStep, () => {
  nextTick(() => updateSpotlight({ scroll: true }));
});
</script>

<template>
  <main
    class="h-full min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden bg-n-background p-6 md:p-8"
  >
    <section
      class="mx-auto flex w-full max-w-7xl flex-col gap-6 text-n-slate-12"
    >
      <header
        data-tour-id="overview"
        class="flex flex-col gap-4 border-b border-n-weak pb-6 xl:flex-row xl:items-end xl:justify-between"
      >
        <div class="max-w-3xl">
          <div
            class="mb-3 inline-flex items-center gap-2 rounded-lg border border-n-weak bg-n-solid-2 px-3 py-1 text-xs font-medium uppercase text-n-slate-11"
          >
            <span class="i-lucide-sparkles size-3.5" />
            {{ pageCopy.eyebrow }}
          </div>
          <h1 class="text-3xl font-semibold tracking-normal md:text-4xl">
            {{ pageCopy.title }}
          </h1>
          <p class="mt-3 max-w-2xl text-base leading-7 text-n-slate-11">
            {{ pageCopy.subtitle }}
          </p>
        </div>
        <div
          class="grid w-full shrink-0 grid-cols-1 gap-2 sm:grid-cols-3 xl:w-[42rem]"
        >
          <a
            href="/hc/manual/pt-BR"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex h-11 w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-n-weak bg-n-solid-2 px-4 text-sm font-semibold text-n-slate-12 shadow-sm transition hover:border-n-strong hover:bg-n-alpha-2"
          >
            <span class="i-lucide-book-open size-4" />
            {{ pageCopy.manualAction }}
          </a>
          <button
            type="button"
            class="inline-flex h-11 w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-n-strong bg-n-solid-1 px-4 text-sm font-semibold text-n-slate-12 shadow-sm transition hover:border-n-brand/50 hover:bg-n-brand/5 hover:text-n-brand"
            @click="startIntro()"
          >
            <span class="i-lucide-map size-4" />
            {{ pageCopy.guidedAction }}
          </button>
          <button
            type="button"
            class="inline-flex h-11 w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-n-brand bg-n-brand px-4 text-sm font-semibold text-white shadow-sm transition hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
            @click="openRoute(channelRoute('whatsapp'))"
          >
            <span class="i-lucide-play size-4" />
            {{ pageCopy.primaryAction }}
          </button>
        </div>
      </header>

      <section class="grid gap-3 md:grid-cols-3">
        <div
          v-for="stat in quickStats"
          :key="stat.label"
          class="flex items-center gap-3 rounded-lg border border-n-weak bg-n-solid-2 p-4"
        >
          <span
            class="grid size-10 place-content-center rounded-lg bg-n-alpha-2"
            :class="stat.tone"
          >
            <span class="size-5" :class="stat.icon" />
          </span>
          <div class="min-w-0">
            <p class="text-xs uppercase text-n-slate-10">{{ stat.label }}</p>
            <p class="truncate text-lg font-semibold text-n-slate-12">
              {{ stat.value }}
            </p>
          </div>
        </div>
      </section>

      <section class="grid gap-4 lg:grid-cols-3">
        <article
          v-for="step in primarySteps"
          :key="step.number"
          :data-tour-id="step.tourId"
          class="flex min-h-[16rem] flex-col justify-between rounded-lg border border-n-weak bg-n-solid-2 p-5"
        >
          <div>
            <div class="mb-5 flex items-center justify-between gap-3">
              <span class="text-sm font-semibold text-n-slate-10">
                {{ step.number }}
              </span>
              <span
                class="inline-flex items-center rounded-md border px-2 py-1 text-xs font-medium"
                :class="step.accent"
              >
                {{ step.status }}
              </span>
            </div>
            <span
              class="mb-4 grid size-11 place-content-center rounded-lg bg-n-alpha-2 text-n-slate-12"
            >
              <span class="size-5" :class="step.icon" />
            </span>
            <h2 class="text-xl font-semibold">{{ step.title }}</h2>
            <p class="mt-3 text-sm leading-6 text-n-slate-11">
              {{ step.description }}
            </p>
          </div>
          <button
            type="button"
            class="mt-6 inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-n-weak px-3 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2"
            @click="openRoute(step.route)"
          >
            {{ step.action }}
            <span class="i-lucide-arrow-right size-4" />
          </button>
        </article>
      </section>

      <section class="grid gap-4 lg:grid-cols-2">
        <article
          v-for="step in growthSteps"
          :key="step.title"
          :data-tour-id="step.tourId"
          class="flex items-start justify-between gap-4 rounded-lg border border-n-weak bg-n-solid-2 p-5"
        >
          <div class="flex min-w-0 gap-4">
            <span
              class="grid size-11 flex-shrink-0 place-content-center rounded-lg bg-n-alpha-2 text-n-slate-12"
            >
              <span class="size-5" :class="step.icon" />
            </span>
            <div class="min-w-0">
              <h2 class="text-base font-semibold">{{ step.title }}</h2>
              <p class="mt-2 text-sm leading-6 text-n-slate-11">
                {{ step.description }}
              </p>
            </div>
          </div>
          <button
            type="button"
            class="inline-flex h-9 flex-shrink-0 items-center justify-center gap-2 rounded-lg border border-n-weak px-3 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2"
            @click="openRoute(step.route)"
          >
            {{ step.action }}
            <span class="i-lucide-arrow-right size-4" />
          </button>
        </article>
      </section>
    </section>

    <Teleport to="body">
      <div v-if="isIntroOpen" class="fixed inset-0 z-[9999]">
        <div class="absolute inset-0 bg-slate-950/70" />
        <div
          class="pointer-events-none fixed rounded-xl border-2 border-n-brand bg-white/5 shadow-[0_0_0_9999px_rgba(2,6,23,0.68),0_0_0_6px_rgba(59,130,246,0.18)] transition-all duration-200"
          :style="spotlightStyle"
        />
        <section
          class="fixed rounded-xl border border-n-weak bg-n-solid-1 p-4 text-n-slate-12 shadow-2xl transition-all duration-200"
          :style="tooltipStyle"
        >
          <div class="mb-3 flex items-center justify-between gap-3">
            <span class="text-xs font-semibold uppercase text-n-slate-10">
              {{ introProgressText }}
            </span>
            <button
              type="button"
              :title="pageCopy.skip"
              class="grid size-7 place-content-center rounded-lg text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12"
              @click="closeIntro"
            >
              <span class="i-lucide-x size-4" />
            </button>
          </div>
          <h2 class="text-lg font-semibold">{{ currentIntroStep.title }}</h2>
          <p class="mt-2 text-sm leading-6 text-n-slate-11">
            {{ currentIntroStep.description }}
          </p>
          <div class="mt-4 flex flex-wrap items-center justify-between gap-2">
            <button
              type="button"
              class="inline-flex h-9 items-center gap-2 rounded-lg border border-n-weak px-3 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="activeIntroStep === 0"
              @click="previousIntroStep"
            >
              <span class="i-lucide-arrow-left size-4" />
              {{ pageCopy.previous }}
            </button>
            <div class="flex gap-2">
              <button
                v-if="currentIntroStep.route"
                type="button"
                class="inline-flex h-9 items-center gap-2 rounded-lg border border-n-weak px-3 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2"
                @click="openCurrentIntroRoute"
              >
                {{ pageCopy.openStep }}
              </button>
              <button
                type="button"
                class="inline-flex h-9 items-center gap-2 rounded-lg bg-n-brand px-3 text-sm font-medium text-white hover:brightness-95"
                @click="nextIntroStep"
              >
                {{ isLastIntroStep ? pageCopy.finish : pageCopy.next }}
                <span class="i-lucide-arrow-right size-4" />
              </button>
            </div>
          </div>
        </section>
      </div>
    </Teleport>
  </main>
</template>
